import Axios, {AxiosError, AxiosRequestConfig, AxiosResponse} from "axios"
import _ from "lodash"
import PubSub from "pubsub-js"
import {router} from "@/router"

export enum MfaType {
  EmlOtp = "EmlOtp",
  AtTotp = "AtTotp",
}

export const MFA_REQUIRED_MESSAGE = "MFA required"

export interface MfaSetup {
  mfaType?: MfaType | null
  code?: string | null
}

export interface MfaResponse {
  message: string
  secret?: string | null
  qrCodeImage?: string | null
}

export enum AuthenticationResult {
  Success,
  ActivationRequired,
}

export enum UserRole {
  CompanyRep,
  AuthorizedRep,
  ProviderRep,
  Application,
  Staff,
  Admin,
  DevOps,
  BrowserClient,
}

export type AuthenticationRequiredReason = "UNAUTHENTICATED" | "LOGOUT" | "SERVER_UNAVAILABLE"

export class AuthenticationService {
  private static readonly AUTHENTICATION_REQUIRED_TOPIC = "authentication-required"

  private static readonly AUTH_BASE_PATH = "/api/authentication"
  private static readonly LOGOUT_URL = "/api/authentication/logout"
  private static isRegistered = false
  private static retryRequests = Array<any>()

  _user: User | null = null
  apiContext: string | null = null

  get user(): User | null {
    return this._user
  }

  static addAuthenticationInterceptor(): void {
    if (AuthenticationService.isRegistered) {
      return
    }

    Axios.interceptors.response.use(
      response => response,
      async (error: AxiosError<any>) => {
        // Response is not defined when is it aborted for instance
        if (error.response) {
          console.log("Response rejected", error.response.request)

          const status = error.response.status

          if (this.isUnauthenticatedRetryable(error)) {
            console.log(`Will retry ${error.response.config.url!} after authentication`)

            const deferred = new Promise(resolve => {
              this.retryRequests.push({
                config: error.config,
                resolve,
              })
            })

            this.raiseAuthenticationRequiredEvent("UNAUTHENTICATED")

            return deferred
          } else if (
            status === 503 ||
            ((status === 500 || status === 502) && this.isAuthCheck(error.config!))
          ) {
            this.raiseAuthenticationRequiredEvent("SERVER_UNAVAILABLE")
          } else if (status === 400) {
            console.error("BAD_REQUEST", error.response.data.errors)
          }
        }

        return Promise.reject(error)
      }
    )

    AuthenticationService.isRegistered = true
  }

  static addAuthenticationRequiredListener(
    listener: (reason: AuthenticationRequiredReason) => void
  ): void {
    PubSub.subscribe(this.AUTHENTICATION_REQUIRED_TOPIC, (message: string, data: any) =>
      listener(data)
    )
  }

  private static isUnauthenticatedRetryable(error: AxiosError<any>): boolean {
    const response = error.response!
    return (
      response.status === 401 &&
      response.config.url !== this.LOGOUT_URL &&
      (error.config?.url !== this.AUTH_BASE_PATH || error.config.method !== "post")
    )
  }

  private static isAuthCheck(config: AxiosRequestConfig): boolean {
    return config.url === this.AUTH_BASE_PATH && config.method === "get"
  }

  private static raiseAuthenticationRequiredEvent(reason: AuthenticationRequiredReason) {
    PubSub.publish(this.AUTHENTICATION_REQUIRED_TOPIC, reason)
  }

  async isAuthenticated(): Promise<boolean> {
    // TODO instead of hitting the backend it should check if the token has been
    // expired based on the return of the authentication
    return Axios.get(AuthenticationService.AUTH_BASE_PATH)
      .then((response: AxiosResponse) => {
        if (_.isEmpty(this.apiContext) && !_.isEmpty(response.data.apiContext)) {
          this.apiContext = response.data.apiContext
          Axios.interceptors.request.use(
            config => {
              if (!config.url!.startsWith("/api/authentication")) {
                config.url = config.url!.replace(/^\/api/, `/${this.apiContext!}`)
              }

              return config
            },
            (error: AxiosError<any>) => {
              throw error
            }
          )
        }

        return true
      })
      .catch(() => false)
  }

  async authenticate(
    username: string,
    password: string,
    mfaCode?: string
  ): Promise<AuthenticationResult> {
    const handleResponse = (response: AxiosResponse) => {
      console.log("Login response", response)

      if (response.status === 200) {
        this.performAuthenticationSucceed()

        return AuthenticationResult.Success
      } else if (response.status === 412) {
        console.log("Activation required")
        return AuthenticationResult.ActivationRequired
      } else {
        throw new Error(this.mapResponseToErrorMessage(response))
      }
    }

    return Axios.post(AuthenticationService.AUTH_BASE_PATH, {
      username,
      password,
      mfaCode,
    })
      .then(handleResponse)
      .catch(error => handleResponse(error.response))
  }

  async logout(): Promise<AxiosResponse> {
    return Axios.post(AuthenticationService.LOGOUT_URL).finally(() => {
      AuthenticationService.raiseAuthenticationRequiredEvent("LOGOUT")
    })
  }

  async requestPasswordReset(email: string): Promise<string> {
    return Axios.post(this.authPath("/passwordResetLink"), {email})
      .then(response => response.data)
      .catch(error =>
        error.response.status === 400 ? "Invalid email address" : error.response.data.message
      )
  }

  async setNewPassword(password: string, confirmPassword: string, token: string): Promise<string> {
    return Axios.post(this.authPath("/passwordReset"), {
      password,
      confirmPassword,
      token,
    })
      .then(response => response.data)
      .catch(error =>
        error.response.status === 400 ? "Unable to reset password" : error.response.data.message
      )
  }

  async updatePassword(
    oldPassword: string,
    newPassword: string,
    mfaCode?: string
  ): Promise<string> {
    return Axios.post(this.authPath("/passwordUpdate"), {
      oldPassword,
      newPassword,
      mfaCode,
    })
      .then(response => response.data)
      .catch(error => {
        const {status, data} = error.response
        if (status === 428) return MFA_REQUIRED_MESSAGE
        if (status === 400) return "Unable to change password"
        return typeof data === "string" ? data : data.message
      })
  }

  async mfaSetup(request: MfaSetup): Promise<MfaResponse> {
    return Axios.post(this.authPath("/mfaSetup"), request).then(response => response.data)
  }

  async validateToken(token: string): Promise<AxiosResponse> {
    return Axios.get(this.authPath("/validateToken"), {
      params: {token},
    })
      .then(response => response)
      .catch(error => {
        if (error.response.status > 400) {
          console.log(error.response.data.message)
        }

        return error.response
      })
  }

  async loadCurrentUser(): Promise<any> {
    return Axios.get(AuthenticationService.AUTH_BASE_PATH).then(response => {
      this._user = response.data.user
      return this._user
    })
  }

  async isStaff(): Promise<boolean> {
    const user = await this.loadCurrentUser()
    if (user == null) {
      await router.push("/login")
      return false
    } else {
      return [
        UserRole[UserRole.AuthorizedRep],
        UserRole[UserRole.Staff],
        UserRole[UserRole.Admin],
      ].includes(user.role)
    }
  }

  async isAdmin(): Promise<boolean> {
    const user = await this.loadCurrentUser()
    if (user == null) {
      await router.push("/login")
      return false
    } else {
      return [UserRole[UserRole.Staff], UserRole[UserRole.Admin]].includes(user.role)
    }
  }

  async isSuper(): Promise<boolean> {
    const user = await this.loadCurrentUser()
    if (user == null) {
      await router.push("/login")
      return false
    } else {
      return user.role === UserRole[UserRole.Admin]
    }
  }

  async isEmployer(): Promise<boolean> {
    const user = await this.loadCurrentUser()
    if (user == null) {
      await router.push("/login")
      return false
    } else {
      return user.role === UserRole[UserRole.CompanyRep]
    }
  }

  private performAuthenticationSucceed() {
    const event = new Event("event:authenticated")
    document.dispatchEvent(event)

    this.retryQueuedRequests()
  }

  private retryQueuedRequests() {
    AuthenticationService.retryRequests.forEach(async (request: any) => {
      console.log("Retrying request:", request.config)
      await Axios.request(request.config).then(value => request.resolve(value))
      // TODO what if this also fails?
    })

    AuthenticationService.retryRequests = []
  }

  private mapResponseToErrorMessage(response: AxiosResponse): string {
    let message

    if (response.status === 401) {
      message = response.data.message
    } else if (response.status >= 500) {
      message = "The server is currently down for routine maintenance, please try again soon"
    } else {
      message = "Unable to login"
    }

    return message
  }

  private authPath(path: string): string {
    return AuthenticationService.AUTH_BASE_PATH + path
  }
}
